import { useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';

import { adaptPageURLToProductsQueryActiveState } from 'src/adapters/pageURLToStore/adaptPageURLToProductsQueryActiveState';
import { adaptPageURLToStore } from 'src/adapters/pageURLToStore/adaptPageURLToStore';
import { parseFilterPath } from 'src/adapters/pageURLToStore/parseFilterPath';
import {
  adaptStoreToFilterPath,
  adaptStoreToPageURL,
} from 'src/adapters/storeToPageURL/adaptStoreToPageURL';
import { serializeFilterPath } from 'src/adapters/storeToPageURL/serializeFilterPath';
import { serializeURLSearchParamsStably } from 'src/adapters/storeToPageURL/serializeURLSearchParamsStably';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { type RequestProductsMetadata, TriggerName } from 'src/redux/products/types';
import {
  type SyncProductsQueryFromURLPayload,
  actions,
  applyURLActiveStateToProductsQuery,
} from 'src/redux/productsQuery/slice';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { AppThunkDispatch } from 'src/redux/store';
import { PageType } from 'src/types/navigation';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';
import { consumePendingProductsQueryURLSyncIntent } from './productsQueryURLSyncIntent';

type ProductsQueryURLSyncState = {
  navigation: Pick<ReduxApplicationState['navigation'], 'pageType'>;
  productsQuery: ReduxApplicationState['productsQuery'];
};

const toStringArray = (value: string | string[] | undefined): string[] =>
  value === undefined ? [] : Array.isArray(value) ? value : [value];

const mergeDeliveryDaysIntoFilterPathValues = (
  filterPathValues: Record<string, string[]>,
  processedQuery: ParsedUrlQuery,
): Record<string, string[]> => {
  const ddFromQuery = toStringArray(processedQuery.dd);
  if (ddFromQuery.length === 0) {
    return filterPathValues;
  }

  const existingDd = filterPathValues.dd ?? [];
  const merged = Array.from(new Set([...existingDd, ...ddFromQuery]));
  return { ...filterPathValues, dd: merged };
};

const serializeProductsQuery = (
  state: ProductsQueryURLSyncState,
  productsQuery: ReduxApplicationState['productsQuery'],
): string => {
  const filterPath = serializeFilterPath(productsQuery.facets);
  const queryStr = serializeURLSearchParamsStably(
    adaptStoreToPageURL(
      {
        ...state,
        productsQuery,
      },
      productsQuery.page,
    ),
  );
  return queryStr ? `${filterPath}?${queryStr}` : filterPath;
};

export const getFacetChangeMetadata = (
  currentFacets: Facet[],
  nextActiveFacetValuesByKey: SyncProductsQueryFromURLPayload['activeFacetValuesByKey'],
): RequestProductsMetadata | undefined => {
  const changedOptions = currentFacets.flatMap((currentFacet) => {
    const nextActiveValues = new Set(nextActiveFacetValuesByKey[currentFacet.alias] ?? []);

    return currentFacet.options.flatMap((currentOption) => {
      const nextActive = nextActiveValues.has(currentOption.value);

      if (currentOption.active === nextActive) {
        return [];
      }

      return [
        {
          facetTitle: currentFacet.title,
          optionActive: nextActive,
          optionTitle: currentOption.title,
        },
      ];
    });
  });

  return changedOptions.length === 1 ? changedOptions[0] : undefined;
};

export const getProductsQueryStateFromURL = (
  asPath: string,
  query: ParsedUrlQuery,
  state: ProductsQueryURLSyncState,
): SyncProductsQueryFromURLPayload => {
  const processedQuery = adaptPageURLToStore(query);
  const filterPathValues = mergeDeliveryDaysIntoFilterPathValues(
    parseFilterPath(asPath),
    processedQuery,
  );

  return adaptPageURLToProductsQueryActiveState(processedQuery, {
    filterPathValues,
    includeSearchTerm: state.navigation.pageType === PageType.Search,
  });
};

export const productsQueryMatchesURL = (
  asPath: string,
  query: ParsedUrlQuery,
  state: ProductsQueryURLSyncState,
): boolean => {
  const nextActiveState = getProductsQueryStateFromURL(asPath, query, state);
  const current = serializeProductsQuery(state, state.productsQuery);
  const next = serializeProductsQuery(
    state,
    applyURLActiveStateToProductsQuery(state.productsQuery, nextActiveState),
  );

  return current === next;
};

export const getRouteChangeTriggerName = (
  state: ProductsQueryURLSyncState,
  nextActiveState: SyncProductsQueryFromURLPayload,
): TriggerName => {
  const nextProductsQuery = applyURLActiveStateToProductsQuery(
    state.productsQuery,
    nextActiveState,
  );

  const currentFilterPath = adaptStoreToFilterPath(state);
  const nextFilterPath = adaptStoreToFilterPath({ ...state, productsQuery: nextProductsQuery });

  const current = adaptStoreToPageURL(state, state.productsQuery.page);
  const next = adaptStoreToPageURL(
    {
      ...state,
      productsQuery: nextProductsQuery,
    },
    nextProductsQuery.page,
  );

  const currentSort = current.get('sort');
  const nextSort = next.get('sort');
  const currentPage = current.get('page');
  const nextPage = next.get('page');

  if (
    currentPage !== nextPage &&
    currentFilterPath === nextFilterPath &&
    currentSort === nextSort
  ) {
    return TriggerName.PAGE_NUMBER_CHANGE;
  }

  if (currentSort !== nextSort && currentFilterPath === nextFilterPath) {
    return TriggerName.CHANGE_SORT;
  }

  return TriggerName.CHANGE_FACETS;
};

export const useSyncProductsQueryFromUrl = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const router = useRouter();
  const lastHandledAsPath = useRef<string | null>(null);
  const pageType = useSelector((state: ReduxApplicationState) => state.navigation.pageType);
  const productsQuery = useSelector((state: ReduxApplicationState) => state.productsQuery);

  useEffect(() => {
    const urlSyncState: ProductsQueryURLSyncState = {
      navigation: {
        pageType,
      },
      productsQuery,
    };

    if (!router.isReady) {
      return;
    }

    if (lastHandledAsPath.current === router.asPath) {
      return;
    }

    lastHandledAsPath.current = router.asPath;

    if (productsQueryMatchesURL(router.asPath, router.query, urlSyncState)) {
      return;
    }

    const nextActiveState = getProductsQueryStateFromURL(router.asPath, router.query, urlSyncState);

    batch(() => {
      const urlSyncIntent = consumePendingProductsQueryURLSyncIntent(router.asPath);
      const triggerName =
        urlSyncIntent?.triggerName ?? getRouteChangeTriggerName(urlSyncState, nextActiveState);
      const metadata =
        urlSyncIntent?.metadata ??
        (triggerName === TriggerName.CHANGE_FACETS
          ? getFacetChangeMetadata(productsQuery.facets, nextActiveState.activeFacetValuesByKey)
          : undefined);

      dispatch(actions.syncFromURL(nextActiveState));
      dispatch(
        requestProducts({
          metadata,
          pageLoading: nextActiveState.page,
          triggerName,
        }),
      );
    });

    scrollToTopOfProductsList();
  }, [dispatch, pageType, productsQuery, router.asPath, router.isReady, router.query]);
};
